import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getSettings } from "../services/settingsService";
import { getCommercialCatalog } from "../services/commercialContextService";
import { setGlobalDateFormat } from '../utils/dateFormatter';
import { setGlobalCurrencyCode } from '../utils/currencyUtils';

const SettingsContext = createContext();

const isActiveRecord = (record) => {
  if (!record || typeof record !== 'object') return false;
  if (record.is_active == null) return true;
  return ![false, 0, '0', 'false'].includes(record.is_active);
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [commercialCatalog, setCommercialCatalog] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSettings = async () => {
    try {
      const data = await getSettings();
      setGlobalDateFormat(data?.date_format || 'DD/MM/YYYY');
      setGlobalCurrencyCode(data?.currency_code || 'INR');
      setSettings(data);
    } catch (err) {
      console.error("❌ Failed to load global settings:", err);
    }
    setLoading(false);
  };

  const loadOperationalContext = async () => {
    if (!localStorage.getItem('token')) {
      setCommercialCatalog(null);
      return null;
    }

    try {
      const data = await getCommercialCatalog();
      setCommercialCatalog(data || {});
      return data || {};
    } catch (err) {
      // Operational gating is deliberately fail-open if the catalog endpoint is
      // unavailable. Permission checks still apply, and the backend remains the
      // source of truth for active commercial context records.
      console.warn('Failed to load commercial context catalog:', err?.message || err);
      setCommercialCatalog(null);
      return null;
    }
  };

  useEffect(() => {
    loadSettings();
    loadOperationalContext();
  }, []);

  const operationalAvailability = useMemo(() => {
    if (!commercialCatalog) {
      return { loaded: false, website: true };
    }

    const activeStorefronts = (commercialCatalog.storefronts || []).filter(isActiveRecord);
    return {
      loaded: true,
      website: activeStorefronts.length > 0,
      activeStorefronts,
    };
  }, [commercialCatalog]);

  const isOperationalModuleAvailable = (moduleKey) => {
    if (String(moduleKey || '').toLowerCase() === 'website') {
      return operationalAvailability.website !== false;
    }
    return true;
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        reloadSettings: loadSettings,
        commercialCatalog,
        operationalAvailability,
        isOperationalModuleAvailable,
        reloadOperationalContext: loadOperationalContext,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
